import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./LoginStyles.css";

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("request"); // 'request', 'verify', 'reset', 'success'
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/forgot-password`,
        { email },
      );
      const sent = response.data?.success === true;
      setMessage(
        response.data.message || "Verification code sent to your email",
      );
      if (sent) {
        setStep("verify");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to send verification code",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/reset-password`,
        {
          email,
          code: verificationCode,
          newPassword,
        },
      );
      setMessage(response.data.message || "Password reset successful");
      setStep("success");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
      }}
    >
      <div
        className="login-container"
        style={{ maxWidth: 440, width: "100%", padding: 24, margin: 16 }}
      >
        <div
          className="login-card"
          style={{
            background: "white",
            borderRadius: 16,
            padding: 40,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {step === "request" && (
            <>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#111827",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Forgot Password?
              </h2>
              <p
                style={{
                  fontSize: 15,
                  color: "#6b7280",
                  marginBottom: 24,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Enter your email address and we'll send you a verification code
                to reset your password.
              </p>

              {error && (
                <div
                  style={{
                    padding: 12,
                    background: "#fee2e2",
                    color: "#b91c1c",
                    borderRadius: 8,
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  {error}
                </div>
              )}

              {message && (
                <div
                  style={{
                    padding: 12,
                    background: "#d1fae5",
                    color: "#065f46",
                    borderRadius: 8,
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  {message}
                </div>
              )}

              <form onSubmit={handleRequestReset}>
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 15,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                </button>
              </form>

              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button
                  onClick={() => navigate("/login")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#2d6da3",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  ← Back to Login
                </button>
              </div>
            </>
          )}

          {step === "verify" && (
            <>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#111827",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Reset Password
              </h2>
              <p
                style={{
                  fontSize: 15,
                  color: "#6b7280",
                  marginBottom: 24,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Enter the verification code sent to <strong>{email}</strong> and
                your new password.
              </p>

              {error && (
                <div
                  style={{
                    padding: 12,
                    background: "#fee2e2",
                    color: "#b91c1c",
                    borderRadius: 8,
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleVerifyAndReset}>
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    required
                    maxLength="6"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 15,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength="8"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 15,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#374151",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    minLength="8"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 15,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>

              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button
                  onClick={() => setStep("request")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#2d6da3",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  ← Resend Code
                </button>
              </div>
            </>
          )}

          {step === "success" && (
            <>
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
                <h2
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    marginBottom: 8,
                    color: "#111827",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Password Reset Successful!
                </h2>
                <p
                  style={{
                    fontSize: 15,
                    color: "#6b7280",
                    marginBottom: 24,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Your password has been reset. Redirecting to login...
                </p>
                <button
                  onClick={() => navigate("/login")}
                  style={{
                    padding: "14px 32px",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Go to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
